/**
 * Admin Wave AW7 — カラムマッピング UI (docs/108 Critical #9)。
 *
 * old2 ImportPage の Step2「カラムマッピング」section を v2 shared-ui で再構成。
 * 各 target field に CSV ヘッダ → field の `<select>` を出し、autoDetectMapping の結果を初期値とする。
 * 「自動検出に戻す」「全クリア」ボタン付き。
 *
 * post-* mode 専用 (participant / session は本パネルを表示しない。それらは固定 schema 直 import)。
 */

import { Button, Card } from '@citizen-walk/shared-ui';
import type { DistrictMode } from '@citizen-walk/shared-types';
import {
  TARGET_FIELDS,
  isFieldVisibleForMode,
  type ColumnMapping,
  type MappingFieldKey,
  type TargetFieldDef,
} from './columnMapping';

export interface ColumnMappingPanelProps {
  headers: string[];
  mapping: ColumnMapping;
  mode: DistrictMode | null;
  disabled?: boolean;
  onChange: (key: MappingFieldKey, value: string | null) => void;
  onAutoDetect: () => void;
  onClear: () => void;
}

const GROUP_LABEL: Record<NonNullable<TargetFieldDef['group']>, string> = {
  post: '投稿フィールド',
  user: 'ユーザー属性（投稿の extraFields に保存）',
  agree: 'いいね',
};

const GROUP_ORDER: NonNullable<TargetFieldDef['group']>[] = ['post', 'user', 'agree'];

export function ColumnMappingPanel({
  headers,
  mapping,
  mode,
  disabled,
  onChange,
  onAutoDetect,
  onClear,
}: ColumnMappingPanelProps) {
  const visibleFields = TARGET_FIELDS.filter((f) => isFieldVisibleForMode(f, mode));
  const locationMapped = !!mapping.latitude || !!mapping.longitude || !!mapping.locationCombined;

  return (
    <Card padding="lg" className="mb-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-base font-bold text-slate-900">2-B. カラムマッピング</h2>
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={onAutoDetect}
            disabled={disabled}
          >
            自動検出に戻す
          </Button>
          <Button type="button" size="sm" variant="secondary" onClick={onClear} disabled={disabled}>
            全クリア
          </Button>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        CSV / Spreadsheet
        の列名と投稿フィールドの対応を設定します。自動検出済みの項目を確認・調整できます。
        マッピング後の内部列名 (lat / lng / createdAt 等) で importExecutor に渡されるため、CSV
        ヘッダ名が AppSheet 等で異なっていても投稿として認識されます。
      </p>

      {GROUP_ORDER.map((group) => {
        const fields = visibleFields.filter((f) => (f.group ?? 'post') === group);
        if (fields.length === 0) return null;
        return (
          <div key={group} className="mb-4 last:mb-0">
            <h3 className="text-xs font-semibold text-slate-600 mb-2">{GROUP_LABEL[group]}</h3>
            <div className="grid grid-cols-1 gap-2">
              {fields.map((f) => (
                <div key={f.key} className="grid grid-cols-[10rem_1fr] gap-2 items-center">
                  <label htmlFor={`map-${f.key}`} className="text-sm text-slate-700">
                    {f.label}
                    {f.required && <span className="text-danger-700 ml-0.5">*</span>}
                  </label>
                  <select
                    id={`map-${f.key}`}
                    value={mapping[f.key] ?? ''}
                    onChange={(e) => onChange(f.key, e.target.value || null)}
                    disabled={disabled}
                    className="min-h-touch px-2 border border-slate-300 rounded text-sm bg-white"
                  >
                    <option value="">（未選択）</option>
                    {headers.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {!locationMapped && (
        <p className="text-xs text-warning-700 mt-2">
          位置列 (緯度/経度 または 結合列) が未マッピングです。位置情報なしの行は暫定座標 (0, 0)
          で登録されます。
        </p>
      )}
      {!mapping.title && !mapping.comment && (
        <p className="text-xs text-danger-700 mt-2">
          タイトル列またはコメント列のいずれかが必須です。
        </p>
      )}
    </Card>
  );
}
